import * as React from 'react';
import { Button } from './Button/Button';
import { FormGroup } from './FormGroup/FormGroup';
import { HelpBlock } from './HelpBlock/HelpBlock';
import { getId } from './helper';
import { callAll } from './helper/fp';
import { Label } from './Label/Label';
import { Tooltip, TooltipProps } from './Tooltip/Tooltip';

interface IInputProps {
  onFocus?: (ev: React.FocusEvent<HTMLElement>) => any;
  onBlur?: (ev: React.FocusEvent<HTMLElement>) => any;
  id?: string;
  'aria-describedby'?: string;
  [key: string]: any;
}

export interface IFieldRenderInputProps {
  getInputProps: <OriProps extends IInputProps>(oriProps: OriProps) => OriProps & IInputProps;
}

export interface IFieldInternalProps {
  /** the props to render your input */
  renderInput: (renderInputProps: IFieldRenderInputProps) => JSX.Element;
  /** the id of the input, important for accessibility */
  inputId?: string;
}

export interface FieldProps {
  /** label text */
  label?: React.ReactNode;
  /** className to be added to form group container */
  wrapperClassName?: string;
  /** validation status of the field, is you wish to control this props, pass down null when there is no value */
  status?: 'success' | 'error' | 'warning';
  /** max width of the field */
  maxWidth?: 'small' | 'narrow' | 'wide' | 'wide-thin';
  /** state if the field is compact, i.e. smaller spacing between elements */
  compact?: boolean;
  /** help text for the field */
  helpText?: React.ReactNode;
  /** error help text for the field that will only be displayed if status is error */
  errorHelpText?: React.ReactNode;
  /** specify to show help text if it is available, else helpText will not be shown when input is focused */
  alwaysShowHelpText?: boolean;
  /** specify if the field is required */
  required?: boolean;
  /** specify the tooltip content when input is focused */
  tooltip?: React.ReactNode;
  /** specify the positions that tooltip should avoid */
  tooltipAvoidPositions?: TooltipProps['avoidPositions'];
}

/**
 * `Field` is the wrapper for form control.
 * It is just composition of `FormGroup`, `HelpBlock`, and `Label`.
 * If the composition here doesn't fit your use case, you could compose yourself.
 */
export class Field extends React.Component<
  FieldProps & IFieldInternalProps,
  { isFocused: boolean; fallBackInputId: string; forceHideTooltip: boolean }
> {
  state = {
    fallBackInputId: getId(),
    forceHideTooltip: false,
    isFocused: false,
  };
  /** this is created as container to search for input when tooltip is closed */
  formgroupRef = React.createRef<HTMLDivElement>();

  handleFocus = () => this.setState({ isFocused: true });
  handleBlur = () => this.setState({ isFocused: false, forceHideTooltip: false });
  forceHideTooltip = () =>
    this.setState({ forceHideTooltip: true }, () => {
      const input: HTMLInputElement | HTMLTextAreaElement | null =
        this.formgroupRef.current && this.formgroupRef.current.querySelector('input, textarea');
      if (input) {
        input.focus();
      }
    });

  shouldShowHelpText = () => this.props.alwaysShowHelpText || !this.state.isFocused;

  shouldShowErrorHelpText = () => this.props.status === 'error' && !!this.props.errorHelpText;

  getInputId = (inputId?: string) => inputId || this.props.inputId || this.state.fallBackInputId;

  getHelpTextId = () => `${this.getInputId()}-error`;

  getAriaDescribedBy = (oriDescribedBy?: string) =>
    oriDescribedBy ||
    (this.shouldShowHelpText() && (this.props.helpText || this.shouldShowErrorHelpText())
      ? this.getHelpTextId()
      : undefined);

  getInputProps = <T extends IInputProps>(oriProps: T) => {
    return Object.assign({}, oriProps, {
      'aria-describedby': this.getAriaDescribedBy(oriProps['aria-describedby']),
      id: this.getInputId(oriProps.id),
      onBlur: callAll(oriProps.onBlur, this.handleBlur),
      onFocus: callAll(oriProps.onFocus, this.handleFocus),
    });
  };

  render() {
    const props = this.props;
    const inputNode = props.tooltip ? (
      <Tooltip
        forceHide={this.state.forceHideTooltip}
        showWhenFocus={true}
        avoidPositions={props.tooltipAvoidPositions}
        label={
          <>
            {props.tooltip}
            <Button onClick={this.forceHideTooltip} className="close" variant="link" data-testid="closeTooltip">
              <span className="ico-delete" aria-hidden>
                x
              </span>
              Luk
            </Button>
          </>
        }
        className="has-close"
      >
        {props.renderInput({ getInputProps: this.getInputProps })}
      </Tooltip>
    ) : (
      props.renderInput({ getInputProps: this.getInputProps })
    );

    return (
      <FormGroup
        className={props.wrapperClassName}
        status={props.status}
        maxWidth={props.maxWidth}
        compact={props.compact}
        required={props.required}
        ref={this.formgroupRef}
      >
        {props.label && <Label htmlFor={this.getInputId()}>{props.label}</Label>}
        {inputNode}
        {this.shouldShowHelpText() &&
          (props.helpText ? (
            <HelpBlock role="alert" id={this.getHelpTextId()}>
              {props.helpText}
            </HelpBlock>
          ) : (
            this.shouldShowErrorHelpText() && (
              <HelpBlock role="alert" id={this.getHelpTextId()}>
                {props.errorHelpText}
              </HelpBlock>
            )
          ))}
      </FormGroup>
    );
  }
}

export default Field;
